import Constants from '../../../constants/Constants';
import {fabric} from 'fabric';

interface IArrow extends fabric.Path {
   __corner: string;
   zoomX: number;
   zoomY: number;
   path: any;
}

const halfAngle = 180;
const radToDeg = halfAngle / Math.PI;
const pathConst = 4;

const arrowConst = 5;
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const minArrow = arrowConst * 4;
const arrowHead = `M 0 0 L ${arrowConst} ${arrowConst} L ${arrowConst} -${arrowConst} Z L ${minArrow} 0`; // SVG Path (M: starting coords, L: draw line, Z: return to start and fill)

const calcAngle = (startCoords: fabric.IPoint, endCoords: fabric.IPoint): number => Math.atan2(endCoords.y - startCoords.y, endCoords.x - startCoords.x) * radToDeg;
const calcDistance = (startCoords: fabric.IPoint, endCoords: fabric.IPoint): number => Math.hypot(endCoords.x - startCoords.x, endCoords.y - startCoords.y);

const initializeEventListeners = (canvas: fabric.Canvas): void => {
   const scaling = (event: any): void => {
      if (event.target.name !== Constants.annotation.shape.arrow) {
         return;
      }

      const arrow = event.target as IArrow;
      const mouse = canvas.getPointer(event.e, true);

      if (arrow.__corner === 'ml' && arrow.originX === 'left') {
         const newOrigins = arrow.getPointByOrigin('right', 'center');
         arrow.left = newOrigins.x;
         arrow.top = newOrigins.y;
         arrow.originX = 'right';
      } else if (arrow.__corner === 'mr' && arrow.originX === 'right') {
         const newOrigins = arrow.getPointByOrigin('left', 'center');
         arrow.left = newOrigins.x;
         arrow.top = newOrigins.y;
         arrow.originX = 'left';
      }

      const angle = arrow.__corner === 'ml' ? calcAngle(mouse, arrow.oCoords.mr) : calcAngle(arrow.oCoords.ml, mouse);
      const distance = Math.max(arrow.width * arrow.scaleX, minArrow);
      arrow.set({angle: angle, width: distance, scaleX: 1, scaleY: 1});
      arrow.path[pathConst][1] = distance;
      arrow.pathOffset.x = distance / 2;
   };

   canvas.on('object:scaling', scaling);
};

const turnOnMouseEventListeners = (canvas: fabric.Canvas, setAnnotationExists: (annotationExists: boolean) => void): void => {
   canvas.selection = true;

   const mouseDown = (event: any): void => {
      if (event.target) {
         return; // Clicked on existing object
      }

      canvas.on('mouse:move', mouseMove);
      canvas.on('mouse:up', mouseUp);

      const mouse = canvas.getPointer(event.e);

      const arrow = new fabric.Path(arrowHead, {
         originX: 'right',
         originY: 'center',
         left: mouse.x,
         top: mouse.y,
         stroke: canvas.freeDrawingBrush.color,
         strokeWidth: canvas.freeDrawingBrush.width,
         lockScalingFlip: true,
         fill: canvas.freeDrawingBrush.color,
         name: Constants.annotation.shape.arrow,
         scaleX: 1,
         scaleY: 1,
         strokeUniform: false
      }) as IArrow;
      arrow.setCoords();
      arrow.setControlsVisibility({bl: false, br: false, mb: false, ml: true, mr: true, mt: false, tl: false, tr: false, mtr: false});
      canvas.selection = false;
      canvas.add(arrow);
      canvas.setActiveObject(arrow);
   };

   const mouseMove = (event: any): void => {
      const arrow = canvas.getActiveObject() as IArrow;
      const mouse = canvas.getPointer(event.e);
      const controlsOffset = canvas.freeDrawingBrush.width * 3; // eslint-disable-line @typescript-eslint/no-magic-numbers
      const angle = calcAngle(mouse, {x: arrow.left, y: arrow.top});
      const distance = Math.max(calcDistance(mouse, {x: arrow.left, y: arrow.top}) - controlsOffset, minArrow);
      arrow.set({width: distance, angle: angle, scaleX: 1, scaleY: 1});
      arrow.path[pathConst][1] = distance;
      arrow.pathOffset.x = distance / 2;
      arrow.setCoords();
      canvas.requestRenderAll();
   };

   const mouseUp = (): void => {
      const arrow = canvas.getActiveObject();
      canvas.discardActiveObject();
      canvas.selection = true;
      setAnnotationExists(true);
      canvas.fire('object:added', {target: arrow});
      canvas.requestRenderAll();
      canvas.off('mouse:up', mouseUp);
      canvas.off('mouse:move', mouseMove);
   };

   canvas.defaultCursor = 'crosshair';
   canvas.on('mouse:down', mouseDown);
};

export default {
   turnOnMouseEventListeners,
   initializeEventListeners
};
